import * as Sentry from '@sentry/browser'
import { useQueryClient } from '@tanstack/react-query'
import { useAppContext } from 'context'
import { ensureDefaultAuthSettings } from 'dittoPortalProvider/defaultAuthSettings'
import { useEffect, useRef } from 'react'
import { getRpcAuthParams } from 'utils/rpcAuth'

/** Seeds `__auth_config` once the domain is reachable. */
const useEnsureDefaultAuthSettings = (
  appId: string,
  cloudDomainUrl: string | undefined,
) => {
  const { jwtToken } = useAppContext()
  const queryClient = useQueryClient()
  const seededAppIdRef = useRef<string>()

  useEffect(() => {
    if (!cloudDomainUrl || seededAppIdRef.current === appId) {
      return
    }
    seededAppIdRef.current = appId

    const seed = async () => {
      try {
        await ensureDefaultAuthSettings(
          appId,
          cloudDomainUrl,
          await getRpcAuthParams(jwtToken),
        )
        // Concurrent reads may have cached an empty result before the write.
        await queryClient.invalidateQueries({ queryKey: ['authConfig', appId] })
      } catch (error) {
        seededAppIdRef.current = undefined
        Sentry.withScope((scope) => {
          scope.setTag('ensure-default-auth-settings', appId)
          Sentry.captureException(error)
        })
      }
    }

    seed()
  }, [appId, cloudDomainUrl, jwtToken, queryClient])
}

export default useEnsureDefaultAuthSettings
